import { ReactNode, useEffect, useState } from "react";
import { Button } from "../../components/Buttons";
import { NavItem } from "./NavigationMap";
import { Link, matchPath, NavLink, useLocation, useNavigate } from "react-router-dom";
import EagerSVGIcon, { EagerSVGIconMap } from "../../components/EagerSVG";
import { Trans, useTranslation } from "react-i18next";
import { AuthContextInterface, Loader, PromiseLoader, useModal, usePromiseLoader } from "@surveying-hub-bv/fe-component-library";
import SideMenuDropdownButton from "./SideMenuDropDownButton";
import i18next from "i18next";
import { UserProfileImage } from "../../components/ContentElements";
import { branding } from "../../resources";
import { RebrandedCTADialog } from "../../components/Dialogs";
import { PricingTableCreditsDialog, PricingTableCreditsProduct, SelectPricingTableCreditsDialog } from "../../components/PricingDialogs";


// ==========================================================
// ==================== Side Menu SubMenu ===================
// ==========================================================
// Child link inside the submenu
type SideMenuSubMenuItemProps = {
    label: string
    href: string
    isTooltip?: boolean // If true, shows a tooltip instead of a full link
}
const SideMenuSubMenuItem = ({
    label,
    href,
    isTooltip
}: SideMenuSubMenuItemProps) => (
    <NavLink
        to={href}
        className={({ isActive }) => `flex items-center w-full h-9 rounded-md px-2 text-sm
                ${isTooltip ? "hover:dark:bg-darkMode-layer3color" : "hover:dark:bg-darkMode-layer2color"} transition-colors duration-200
                ${isActive ? 'font-bold dark:text-darkMode-primary' : ''}`}
    >
        <span className="truncate">{label}</span>
    </NavLink>
)

// Root of the submenu
type SideMenuSubMenuProps = {
    label: string
    icon: EagerSVGIconMap
    menuIsOpen: boolean
    isMobile: boolean
    items: SideMenuSubMenuItemProps[]
}
const SideMenuSubMenu = ({
    label,
    icon,
    menuIsOpen,
    isMobile,
    items,
}: SideMenuSubMenuProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(() => {
        const stored = localStorage.getItem(`submenu-open-${label}-${icon}`);
        return stored ? JSON.parse(stored) : false;
    })

    useEffect(() => {
        localStorage.setItem(`submenu-open-${label}-${icon}`, JSON.stringify(isOpen));
    }, [isOpen, icon, label]);

    // Only close the submenu when switching between accordion/hover mode, not on every menuIsOpen/isMobile change
    useEffect(() => {
        if (!(menuIsOpen || isMobile)) {
            setIsOpen(false);
        }
    }, [menuIsOpen, isMobile])

    /** True ⇢ render as accordion,  False ⇢ render as hover‑dropdown. */
    const asAccordion = menuIsOpen || isMobile

    const location = useLocation();

    // `true` if the current URL matches *any* link in this submenu
    const isAnyActive = items.some(({ href }) =>
        matchPath({ path: href, end: false }, location.pathname)
    )

    const toggleAccordion = () => {
        setIsOpen((prev) => !prev);
    }

    return (
        <li className="relative group
            after:absolute after:content-[''] after:left-full after:top-0
             after:w-2 after:h-full
            after:pointer-events-auto">
            {/* Header (button) — always visible */}
            <button
                type="button"
                onClick={toggleAccordion}
                /* Treat the header exactly like a normal link visually */
                className={`flex items-center ${menuIsOpen ? 'w-full' : 'w-fit'}
                    px-2 h-10 rounded-lg
                    hover:dark:bg-darkMode-layer2color transition-colors duration-200
                    focus:outline-none ${isAnyActive ? 'dark:text-darkMode-primary font-bold' : ''}`}
            >
                {/* Icon */}
                <span className="flex-shrink-0">
                    <EagerSVGIcon name={icon} width={24} height={24} />
                </span>

                {/* Label (only when the sidebar is expanded or we are on mobile) */}
                {(menuIsOpen || isMobile) && (
                    <span className="ml-3 text-[14px] truncate">{label}</span>
                )}

                {/* Accordion arrow (only when rendered as accordion and the label is visible) */}
                {asAccordion && (menuIsOpen || isMobile) && (
                    <svg
                        className={`h-4 w-4 ml-auto transition-transform duration-2 00 ${isOpen ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                )}
            </button>

            {/* ----------  Accordion panel  ---------- */}
            {asAccordion && (
                <ul
                    className={`ml-4 pl-4 overflow-hidden
                      ${isOpen ? 'mt-2 max-h-96 opacity-100 border-l dark:border-darkMode-layer3border' : 'max-h-0 opacity-0'}`}
                >
                    {items.map((itm) => (
                        <li key={itm.href} className="mb-1">
                            <SideMenuSubMenuItem {...itm} />
                        </li>
                    ))}
                </ul>
            )}

            {/* ----------  Hover dropdown (desktop, sidebar collapsed)  ---------- */}
            {!asAccordion && (
                <div
                    /* “pointer-events-none” blocks clicks unless the group is hovered,
                       keeping the dropdown from stealing focus in normal state */
                    className="absolute left-full top-0 ml-2 w-48 dark:bg-darkMode-layer2color border border-darkMode-layer3border
                     rounded-md shadow-lg shadow-[rgba(0,0,0,0.8)] opacity-0 translate-y-2
                     group-hover:opacity-100 group-hover:translate-y-0
                     group-hover:pointer-events-auto pointer-events-none
                     transition-all duration-200 z-50"
                >
                    <div className="pt-3 pb-2">
                        <p className="px-4 mb-1 text-[12px] dark:text-darkMode-secondaryText pointer-events-none">{label}</p>
                        {items.map((itm) => (
                            <div key={itm.href} className="px-2 mb-1 last:mb-0">
                                <SideMenuSubMenuItem {...itm} isTooltip={!asAccordion} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </li>
    )
}


// =========================================================
// ================== Quick Links ==========================
// =========================================================
type QuickLinksProps = {
    label: string
    items: {
        label: string
        href: string
        isActive?: boolean
    }[]
    isActive?: boolean
    isSideMenuOpen: boolean
    isMobile: boolean
}
const QuickLinks = ({
    label,
    items,
    isActive,
    isSideMenuOpen,
    isMobile
}: QuickLinksProps) => {
    const showList = isSideMenuOpen || isMobile
    return (
        <div className="mb-4 relative group after:absolute after:content-[''] after:left-full after:top-0 after:w-2 after:h-full after:pointer-events-auto mt-6 md:mt-20">
            <div
                className={`flex items-center ${isSideMenuOpen ? 'w-full' : 'w-fit mx-auto px-2 h-10 rounded-lg hover:dark:bg-darkMode-layer2color transition-colors duration-200 focus:outline-none '} ${isActive ? 'dark:text-darkMode-primary font-bold' : ''}`}
            >
                <span className={`flex-shrink-0 ${isSideMenuOpen || isMobile ? "hidden" : ""}`}>
                    {/* Plus Icon */}
                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="16" />
                        <line x1="8" y1="12" x2="16" y2="12" />
                    </svg>
                </span>
                {(isSideMenuOpen || isMobile) && (
                    <span className="dark:text-darkMode-secondaryText ml-3 text-[14px] truncate mb-2">{label}</span>
                )}
            </div>
            {/* Always show links as list when open or mobile */}
            {showList && (
                <ul className="pl-2">
                    {items.map((itm) => (
                        <li key={itm.href} className="mb-1">
                            <Link
                                to={itm.href}
                                className={`flex items-center gap-x-2 w-full h-9 rounded-md px-2 text-sm
                hover:dark:bg-darkMode-layer2color transition-colors duration-200
                ${isActive ? 'font-bold dark:text-darkMode-primary' : ''}`}
                            >
                                <svg width="22" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="10" cy="10" r="8" />
                                    <line x1="10" y1="6" x2="10" y2="14" />
                                    <line x1="6" y1="10" x2="14" y2="10" />
                                </svg>
                                <span className="truncate">{itm.label}</span>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
            {/* Tooltip hover dropdown when collapsed on desktop */}
            {!showList && (
                <div
                    className="absolute left-full top-0 ml-2 w-48 dark:bg-darkMode-layer3color
                     rounded-md shadow-lg opacity-0 translate-y-2
                     group-hover:opacity-100 group-hover:translate-y-0
                     group-hover:pointer-events-auto pointer-events-none
                     transition-all duration-200 z-50"
                >
                    <div className="py-2">
                        <p className="px-4 mb-1 text-[12px] dark:text-darkMode-secondaryText pointer-events-none">{label}</p>
                        {items.map((itm) => (
                            <div key={itm.href} className="px-2 mb-1 last:mb-0">
                                <SideMenuSubMenuItem {...itm} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}


// =========================================================
// ==================== Side Menu Link =====================
// =========================================================
type SideMenuLinkProps = {
    label: string
    href: string
    icon: EagerSVGIconMap // Needs to be string only later.
    // isActive?: boolean
    menuIsOpen: boolean
    isMobile: boolean
}
const SideMenuLink = ({ label, href, icon, menuIsOpen: isOpen, isMobile }: SideMenuLinkProps) => {
    return (
        <li className="relative group">
            <NavLink
                to={href}
                target={href.includes("http") ? "_blank" : undefined}
                rel={href.includes("http") ? "noopener noreferrer" : undefined}
                className={({ isActive }) => `flex items-center ${isOpen ? "w-full" : "w-fit"} ${isActive ? "dark:text-darkMode-primary" : ""} px-2 h-10 rounded-lg hover:dark:bg-darkMode-layer2color transition-colors duration-200`}
            >
                {({ isActive }: { isActive: boolean }) => (
                    <>
                        <span className="flex-shrink-0 w-[24px]">
                            <EagerSVGIcon name={icon} width={24} height={24} />
                        </span>
                        {(isOpen || isMobile) && (
                            <span className={`text-[14px] ml-3 ${isActive ? "font-bold" : ""}`}>{label}</span>
                        )}
                        {/* Tooltip */}
                        {!isOpen && !isMobile && (
                            <span
                                className="absolute left-full top-1/2 -translate-y-1/2 ml-2 whitespace-nowrap dark:bg-darkMode-layer3color dark:text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50 shadow-lg"
                            >
                                {/* Triangle arrow */}
                                <span
                                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full w-0 h-0 border-y-[6px] border-y-transparent border-r-[10px] dark:border-r-darkMode-layer3color"
                                />
                                {label}
                            </span>
                        )}
                    </>
                )}
            </NavLink>
        </li>
    )
}


type SideMenuProps = {
    navigation: NavItem[]
    loginUrl: string | (() => void)
    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void
    loggedIn?: AuthContextInterface
    isLoggedIn: boolean
    selector: ReactNode[]
    accountPageUrl?: string
    isMobile: boolean
    profileType?: string

    // Double check the PricingTable type as there are multiple definitions in the codebase.
    tokenCheckout: Loader<{ name: string, data: PricingTableCreditsProduct[] }[], unknown>
    reportOpenTokenPricingTable: () => void
    createCheckoutSession: (priceId: string) => Promise<string>
    fetchUserOverview?: () => Promise<{ tokenAmount: number }>
}
export const SideMenu = ({ navigation, loginUrl, isOpen, setIsOpen, loggedIn, isLoggedIn, selector, accountPageUrl, isMobile, profileType, tokenCheckout, reportOpenTokenPricingTable, createCheckoutSession, fetchUserOverview }: SideMenuProps) => {
    const { t } = useTranslation()
    const { showModal } = useModal()
    const navigate = useNavigate()


    const { loader } = usePromiseLoader(
        fetchUserOverview ?? (() => Promise.resolve({ tokenAmount: 0 }))
    )

    // Width logic
    const drawerWidthMobile = "100vw";   // stays constant on phones
    const drawerWidthDesktop = "260px";
    const drawerPeekDesktop = "60px";

    const openWidth = isMobile ? drawerWidthMobile : drawerWidthDesktop;
    const closedWidth = isMobile ? drawerWidthMobile : drawerPeekDesktop;
    const currentWidth = isOpen ? openWidth : closedWidth;

    return (
        <div
            className={`
          fixed top-0 left-0 bottom-0 z-40
          dark:bg-darkMode-layer1color bg-white
          border-r dark:border-darkMode-layer1border

          ${isMobile
                    ? `transition-transform duration-300 ease-in-out
           will-change-transform
           ${isOpen ? "translate-x-0" : "-translate-x-full"}`
                    : ""
                }
        `}
            style={{
                width: currentWidth,
                top: "var(--notif-height, 0px)",
            }}
        >
            {/* Mobile close button */}
            <div className={`absolute top-0 left-0 right-0 md:hidden z-50 dark:bg-darkMode-layer1color border-b dark:border-darkMode-layer1border p-4 ${isOpen ? "translate-x-0" : "-translate-x-20"}`}>
                <button
                    className={`flex flex-col justify-center items-center w-8 h-8`}
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label={isOpen ? "Close menu" : "Open menu"}
                >
                    <svg
                        className="w-6 h-6"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>

            {/* Desktop chevron button */}
            <div className="absolute -right-14 top-1/2 -translate-y-1/2 hidden md:block group">
                <div
                    className="group/button cursor-pointer w-14 h-14 flex items-center justify-center"
                    onClick={() => setIsOpen(!isOpen)}
                    tabIndex={0}
                    role="button"
                    aria-label={isOpen ? "Close menu" : "Open menu"}
                >
                    <button
                        className="z-50 flex justify-center items-center w-8 h-8 transition-all duration-200 rounded-full group-hover/button:dark:bg-darkMode-layer2color"
                        tabIndex={-1}
                        aria-hidden="true"
                        type="button"
                    >
                        <svg
                            className={`w-6 h-6 ${isOpen ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
                {/* Tooltip */}
                <span
                    className={`
              absolute left-12 top-1/2 -translate-y-1/2
              px-2 py-1 rounded dark:bg-darkMode-layer3color text-white text-xs
              opacity-0 group-hover:opacity-100
              pointer-events-none select-none
              whitespace-nowrap
              transition-all duration-150
              shadow-lg
            `}
                >
                    {isOpen ? t("navigation.collapse") : t("navigation.expand")}
                </span>
            </div>

            <div className={`flex flex-col w-full h-full ${isMobile ? "overflow-x-hidden pt-20" : ""}`}>
                {/* Top section: org/user info */}
                {isLoggedIn ? (
                    <div className={`p-2`}>
                        {/* Replace with real user/org info */}
                        <SideMenuDropdownButton
                            label={profileType === "Admin" ? t("navigation.admin") : t("navigation.personal")}
                            tooltipLabel="Profile"
                            icon={
                                // <span className="dark:text-black dark:bg-white rounded-md p-1">
                                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 7V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2" />
                                    <path d="M3 7h18v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
                                </svg>
                                // </span>

                            }
                            sideMenuOpen={isOpen}
                            isMobile={isMobile}
                            options={selector}
                        />
                    </div>
                )
                    :
                    !isOpen && !isMobile
                        ?
                        <Button
                            label={t("auth.logIn")}
                            onClick={() => {
                                if (loggedIn) {
                                    loggedIn.logout()
                                } else if (loginUrl instanceof Function) {
                                    loginUrl();
                                } else {
                                    navigate('/login');
                                }
                            }}
                            icon="LoginIcon"
                            iconOnly={!isOpen && !isMobile}
                            buttonCustomClasses="mx-auto dark:text-black dark:bg-darkMode-primary rounded-md mt-2.5"
                            alwaysFullWidth={isOpen || isMobile}
                        />
                        :
                        // Login Prompt
                        <div className="p-4 border dark:border-darkMode-primary rounded-md flex flex-col items-center justify-center gap-2 my-auto mx-4 mt-14">
                            <h2 className="text-[22px] md:text-[24px] mb-2 text-center break-words"><Trans i18nKey="navigation.loginLead" components={{ span: <span className="dark:text-darkMode-primary" /> }} /></h2>
                            <p className="mb-4 text-center text-[14px] md:text-[16px] dark:text-darkMode-secondaryText"><Trans i18nKey="navigation.loginLeadDesc" components={{ span: <span className="dark:text-darkMode-primary" /> }} /></p>
                            <div className="flex flex-wrap justify-center gap-2 w-full">
                                <Button
                                    label={t("auth.logIn")}
                                    onClick={() => {
                                        if (loggedIn) {
                                            loggedIn.logout()
                                        } else if (loginUrl instanceof Function) {
                                            loginUrl();
                                        } else {
                                            navigate('/login');
                                        }
                                    }}
                                    buttonStyle="secondary"
                                    buttonCustomClasses="h-fit"
                                />
                                <Button
                                    label={t("auth.signUp")}
                                    onClick={() => {
                                        if (loggedIn) {
                                            loggedIn.logout()
                                        } else if (loginUrl instanceof Function) {
                                            loginUrl();
                                        } else {
                                            navigate('/login');
                                        }
                                    }}
                                    buttonStyle="primary"
                                    buttonCustomClasses="h-fit"
                                />
                            </div>
                        </div>}


                {/* Main navigation */}
                <div className={`flex-1 min-h-0 ${isOpen ? "overflow-y-auto" : ""}`}>
                    <ul className={`py-8 md:py-12 flex flex-col gap-y-4 ${isOpen ? "px-2 md:w-[98%]" : "px-2"}`}>
                        {navigation.map((navItem, index) => {
                            if (navItem.type === "link") {
                                return (
                                    <SideMenuLink
                                        key={index}
                                        label={navItem.name}
                                        href={navItem.url ?? ""}
                                        icon={navItem.icon ?? "StackIcon"}
                                        menuIsOpen={isOpen}
                                        isMobile={isMobile}
                                    />
                                );
                            } else if (navItem.type === "submenu") {
                                return (
                                    <SideMenuSubMenu
                                        key={index}
                                        label={navItem.name}
                                        icon={navItem.icon ?? "StackIcon"}
                                        menuIsOpen={isOpen}
                                        isMobile={isMobile}
                                        items={
                                            (navItem.items ?? []).map((item: any) => ({
                                                label: item.name ?? "",
                                                href: item.url ?? "",
                                                isActive: item.isActive
                                            }))
                                        }
                                    />
                                );
                            } else if (navItem.type === "quicklinks") {
                                return (
                                    <QuickLinks
                                        key={index}
                                        label={navItem.name}
                                        items={(navItem.items ?? []).map((item: any) => ({
                                            label: item.name ?? "",
                                            href: item.url ?? "",
                                            isActive: item.isActive
                                        }))}
                                        isSideMenuOpen={isOpen}
                                        isMobile={isMobile}
                                    />
                                );
                            }
                            return null;
                        })}
                    </ul>
                </div>

                {branding.isRebranded && !loggedIn && (
                    <p className={`text-[14px] dark:text-darkMode-mutedText px-2 mb-5 ${!isOpen ? "hidden" : ""}`}>{String(i18next.t('copyright.poweredBy') || "Powered by")} <a href="https://rtkfix.com" target="_blank" className="dark:text-darkMode-hyperLink hover:underline" rel="noreferrer dofollow">RTK FIX</a></p>
                )}

                {/* Footer section */}
                <div className={`relative z-50 dark:bg-darkMode-layer1color mt-auto pt-2 pb-4 px-2 ${isOpen ? "border-t dark:border-darkMode-layer2border" : ""} ${!loggedIn ? "hidden" : ""}`}>
                    {/* Subtle top fade when menu is collapsed */}
                    {!isOpen && (
                        <div
                            className="absolute -top-6 left-0 w-full h-6 pointer-events-none z-10"
                            style={{
                                background: "linear-gradient(to bottom, rgba(24,24,24,0.00) 0%, rgba(24,24,24,0.85) 100%)"
                            }}
                        />
                    )}

                    {/* Credits bar */}
                    {profileType !== "Admin" && isOpen && isLoggedIn &&
                        <PromiseLoader
                            loader={tokenCheckout}
                            // TODO: add mini loading spinner here when component is available.
                            loading={<div className="min-h-[55px] flex items-center gap-3 mb-3 rounded-md dark:bg-darkMode-layer2color border dark:border-darkMode-layer2border dark:hover:border-darkMode-primary transition-all duration-200 cursor-pointer">
                                <div className="animate-pulse flex items-center justify-center space-x-4">
                                    <div className="bg-gray-200 dark:bg-darkMode-layer3color h-4 w-16 rounded ml-2"></div>
                                    <div className="bg-gray-200 dark:bg-darkMode-layer3color h-4 w-32 rounded"></div>
                                </div>
                            </div>}
                            error={<></>}
                            loaded={(pricingTables) => (
                                pricingTables.length !== 0 ? (
                                    <div className="flex items-center justify-between gap-3 mb-3 rounded-md dark:bg-darkMode-layer2color border dark:border-darkMode-layer2border dark:hover:border-darkMode-primary transition-all duration-200 cursor-pointer">
                                        <Link to="/credits" className="flex-1 h-full py-3.5 pl-4">
                                            <span className="text-xs font-medium truncate">
                                                <span className="text-base font-bold dark:text-darkMode-primary text-green-700">
                                                    <PromiseLoader loader={loader} loading={<></>} error={<></>} loaded={(loaded: { tokenAmount?: number }) => loaded.tokenAmount !== undefined ?
                                                        loaded.tokenAmount.toString() : <></>} />
                                                </span>{" "}
                                                <span className="text-xs">{t("navigation.credits")}</span>
                                            </span>
                                        </Link>

                                        <Button
                                            label={t("navigation.buyCredits")}
                                            onClick={
                                                pricingTables.length === 1
                                                    ? () => {
                                                        showModal(<PricingTableCreditsDialog
                                                            products={pricingTables[0].data}
                                                            createCheckoutSession={createCheckoutSession}
                                                        />)
                                                        reportOpenTokenPricingTable()
                                                    }
                                                    : () => {
                                                        showModal(<SelectPricingTableCreditsDialog
                                                            pricingTables={pricingTables}
                                                            createCheckoutSession={createCheckoutSession}
                                                        />)
                                                        reportOpenTokenPricingTable()
                                                    }
                                            }
                                            buttonStyle="secondary"
                                            size="tiny"
                                            alwaysFitWidth
                                            buttonCustomClasses="mr-2"
                                        />
                                    </div>
                                ) : null
                            )}
                        />
                    }

                    {/* Only show for rebranded */}
                    {branding.isRebranded && (
                        <p className={`text-[13px] dark:text-darkMode-mutedText px-2 ${isLoggedIn ? "mb-2" : ""} ${!isOpen ? "hidden" : ""}`}>{String(i18next.t('copyright.poweredBy') || "Powered by")} <a href="https://rtkfix.com" target="_blank" className="dark:text-darkMode-hyperLink hover:underline" rel="noreferrer dofollow">RTK FIX</a></p>
                    )}

                    {isLoggedIn && (
                        <>
                            {/* Replace with real user info */}
                            <SideMenuDropdownButton
                                label={loggedIn?.username ?? "User Name"}
                                subLabel={loggedIn?.email ?? undefined}
                                icon={<UserProfileImage photoURL={loggedIn?.photoURL} />}
                                options={[
                                    <div className="flex items-center px-2 py-2">
                                        <span className="relative flex-shrink-0">
                                            <span className="relative flex items-center justify-center rounded-md w-fit">
                                                <UserProfileImage photoURL={loggedIn?.photoURL} />
                                            </span>
                                        </span>
                                        <div className={`flex flex-col text-left ${isMobile ? "max-w-[200px]" : "max-w-[150px]"} ml-3`}>
                                            <span className="text-[14px] truncate">{loggedIn?.username ?? "User Name"}</span>
                                            {loggedIn?.email && (
                                                <span className="text-[12px] dark:text-darkMode-secondaryText truncate">{loggedIn?.email ?? undefined}</span>
                                            )}
                                        </div>
                                    </div>,

                                    <hr className="border-b-[0px] dark:border-darkMode-layer3border" />,

                                    <>{!branding.isRebranded && (
                                        <>
                                            <div className="p-1">
                                                <button
                                                    onClick={() => showModal(<RebrandedCTADialog loggedInEmail={loggedIn?.email ?? ""} submittedFrom={"Sidemenu CTA"} />)}
                                                    className="cursor-pointer flex items-center w-full p-2 hover:dark:bg-darkMode-layer1color transition-all duration-300 rounded-md"
                                                >
                                                    <EagerSVGIcon name="StarSolidIcon" width={17} height={17} className="mr-2" />
                                                    {t("navigation.upgradeToPro")}
                                                </button>
                                            </div>
                                            <hr className="border-b-[0px] dark:border-darkMode-layer3border" />
                                        </>
                                    )}</>,

                                    <div className="p-1">
                                        <NavLink
                                            to={accountPageUrl + "/billing"}
                                            className="cursor-pointer flex items-center w-full p-2 hover:dark:bg-darkMode-layer1color transition-all duration-300 rounded-md"
                                        >
                                            {() => (
                                                <>
                                                    <EagerSVGIcon name="SettingsIcon" width={18} height={18} className="mr-2" />
                                                    {t("navigation.account")}
                                                </>
                                            )}
                                        </NavLink>

                                        {!branding.isRebranded && (
                                            <NavLink
                                                to="https://rtkfix.com/docs"
                                                target="_blank"
                                                className="cursor-pointer flex items-center w-full p-2 hover:dark:bg-darkMode-layer1color transition-all duration-300 rounded-md"
                                            >
                                                {() => (
                                                    <>
                                                        <EagerSVGIcon name="HelpIcon" width={18} height={18} className="mr-2" />
                                                        {t("navigation.help")}
                                                    </>
                                                )}
                                            </NavLink>
                                        )}

                                        {!branding.isRebranded && (
                                            <NavLink
                                                to="https://rtkfix.com/terms-conditions"
                                                target="_blank"
                                                className="cursor-pointer flex items-center w-full p-2 hover:dark:bg-darkMode-layer1color transition-all duration-300 rounded-md"
                                            >
                                                {() => (
                                                    <>
                                                        <EagerSVGIcon name="DocumentIcon" width={18} height={18} className="mr-2" />
                                                        {t("navigation.policy")}
                                                    </>
                                                )}
                                            </NavLink>
                                        )}
                                    </div>,

                                    <hr className="border-b-[0px] dark:border-darkMode-layer3border" />,
                                    <div className="p-1">
                                        <button
                                            onClick={() => loggedIn?.logout()}
                                            className="cursor-pointer flex items-center w-full p-2 hover:dark:bg-darkMode-layer1color transition-all duration-300 rounded-md"
                                        >
                                            <EagerSVGIcon name="LoginIcon" width={18} height={18} className="mr-2" />
                                            {t("navigation.logOut")}
                                        </button>
                                    </div>
                                ]}
                                sideMenuOpen={isOpen}
                                isMobile={isMobile}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
